import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const KAPATMA_ANAHTARI = "akkuzey_uygulama_kutusu_kapatildi";
const TEKRAR_GOSTERME_GUN = 14;
const GECIKME_MS = 8000; // siteye girer girmez değil, biraz gezindikten sonra

const zatenUygulama = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

// iPhone / iPad Safari: otomatik kurulum yok, kullanıcıya "Ana Ekrana Ekle" adımı gösterilir
const iosSafari = () => {
  const ua = window.navigator.userAgent;
  const ios = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  return ios && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

const yakinZamandaKapatildi = () => {
  try {
    const zaman = Number(localStorage.getItem(KAPATMA_ANAHTARI));
    return zaman && Date.now() - zaman < TEKRAR_GOSTERME_GUN * 864e5;
  } catch {
    return false;
  }
};

/**
 * "Uygulamayı yükle" kutusu.
 * Android / masaüstü Chrome-Edge: tarayıcının kurulum penceresini açan bir düğme.
 * iPhone Safari: Paylaş → Ana Ekrana Ekle adımlarını anlatan kutu.
 * Uygulama zaten kuruluysa ya da kutu yakın zamanda kapatıldıysa görünmez.
 */
export default function UygulamaKur() {
  const { t } = useTranslation();
  const [kurulumIstegi, setKurulumIstegi] = useState(null);
  const [tur, setTur] = useState(null); // "android" | "ios" | null

  useEffect(() => {
    if (zatenUygulama() || yakinZamandaKapatildi()) return;

    let zamanlayici;
    const istekGeldi = (e) => {
      e.preventDefault(); // tarayıcının kendi küçük bildirimi yerine bizim kutumuz
      setKurulumIstegi(e);
      zamanlayici = setTimeout(() => setTur("android"), GECIKME_MS);
    };
    const kuruldu = () => setTur(null);

    window.addEventListener("beforeinstallprompt", istekGeldi);
    window.addEventListener("appinstalled", kuruldu);
    if (iosSafari()) zamanlayici = setTimeout(() => setTur("ios"), GECIKME_MS);

    return () => {
      clearTimeout(zamanlayici);
      window.removeEventListener("beforeinstallprompt", istekGeldi);
      window.removeEventListener("appinstalled", kuruldu);
    };
  }, []);

  const kapat = () => {
    setTur(null);
    try {
      localStorage.setItem(KAPATMA_ANAHTARI, String(Date.now()));
    } catch {
      /* depolama kapalıysa kutu yalnızca bu oturumda kapanır */
    }
  };

  const yukle = async () => {
    if (!kurulumIstegi) return;
    kurulumIstegi.prompt();
    const { outcome } = await kurulumIstegi.userChoice;
    setKurulumIstegi(null);
    if (outcome === "accepted") setTur(null);
    else kapat();
  };

  if (!tur) return null;

  return (
    <div
      role="dialog"
      aria-label={t("install.title")}
      className="yazdir-gizle fixed inset-x-3 bottom-3 z-[95] mx-auto max-w-md rounded-2xl border border-line bg-white p-4 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.45)] sm:inset-x-auto sm:end-5 sm:bottom-5"
    >
      <div className="flex items-start gap-3.5">
        <img src="/icon-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl border border-line" />
        <div className="min-w-0 flex-1 text-start">
          <p className="text-[14.5px] font-semibold text-ink">{t("install.title")}</p>
          <p className="mt-0.5 text-[12.5px] leading-snug text-muted">{t("install.text")}</p>
        </div>
        <button
          type="button"
          onClick={kapat}
          aria-label={t("install.later")}
          className="-me-1 -mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted transition-colors hover:bg-canvas hover:text-ink"
        >
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4" aria-hidden="true">
            <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
          </svg>
        </button>
      </div>

      {tur === "android" ? (
        <div className="mt-3.5 flex gap-2">
          <button type="button" onClick={yukle} className="btn-primary flex-1 justify-center">
            {t("install.button")}
          </button>
          <button
            type="button"
            onClick={kapat}
            className="rounded-md border border-line px-4 text-[13.5px] font-medium text-body transition-colors hover:border-ink hover:text-ink"
          >
            {t("install.later")}
          </button>
        </div>
      ) : (
        <ol className="mt-3.5 space-y-2 rounded-xl bg-canvas px-3.5 py-3 text-[13px] text-body">
          <li className="flex items-center gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white">1</span>
            <span>
              {t("install.iosStep1")}{" "}
              {/* Safari'nin Paylaş simgesi */}
              <svg viewBox="0 0 20 20" fill="none" stroke="#0a84ff" strokeWidth="1.7" className="inline h-4 w-4 -translate-y-px" aria-label={t("install.share")}>
                <path d="M10 2.5v10M6.5 6 10 2.5 13.5 6" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M6 9H4.5v8.5h11V9H14" strokeLinejoin="round" />
              </svg>
            </span>
          </li>
          <li className="flex items-center gap-2.5">
            <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-ink text-[11px] font-semibold text-white">2</span>
            <span>
              {t("install.iosStep2")} <strong className="font-semibold text-ink">{t("install.iosAdd")}</strong>
            </span>
          </li>
        </ol>
      )}
    </div>
  );
}
